import { Component, DestroyRef, OnDestroy, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { datadogRum } from '@datadog/browser-rum';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { GridApi, ICellRendererParams } from 'ag-grid-enterprise';
import { CrossReferenceAssessmentsService } from '../../cross-reference-assessments/cross-reference-assessments.service';
import {
  AssessmentGridData,
  CrossReference,
  CrossReferenceAssessmentUpdates,
  CrossReferencePermissions,
  CrossReferencesOpinionOption,
  Features,
  User,
} from '../../../../types/types';

export interface ClinicalAssessmentGridData extends AssessmentGridData {
  permissions: CrossReferencePermissions;
  user: User;
  productId: number;
  crossReferenceOpinionOptions?: CrossReferencesOpinionOption[];
  features?: Features;
  isShowingSearchResults: boolean;
}

export interface ClinicalAssessmentCellRendererParams
  extends ICellRendererParams {
  data: {
    clinicalAssessmentGridData?: ClinicalAssessmentGridData;
  };
  updateClinicalAssessment?: (crossReference: CrossReference) => {};
  addCross?: (crossReference: CrossReference) => {};
  addNote?: () => {};
  gridApi: GridApi;
}

@Component({
    selector: 'app-grid-clinical-assessment-wrapper',
    templateUrl: './grid-clinical-assessment-wrapper.component.html',
    styleUrls: ['./grid-clinical-assessment-wrapper.component.scss'],
    standalone: false
})
/**
 * The purpose of this is to simply act as an abstraction layer for the actual clinical-assessment component.
 */
export class GridClinicalAssessmentWrapperComponent
  implements ICellRendererAngularComp, OnDestroy
{
  clinicalAssessmentGridData?: ClinicalAssessmentGridData;
  crossReference?: CrossReference;
  gridApi?: GridApi;
  params!: ClinicalAssessmentCellRendererParams;
  features?: Features;
  private destroyRef = inject(DestroyRef);

  constructor(
    private crossReferenceAssessmentsService: CrossReferenceAssessmentsService,
  ) {}

  agInit(params: ClinicalAssessmentCellRendererParams): void {
    this.params = params;
    this.clinicalAssessmentGridData =
      params.data?.clinicalAssessmentGridData ||
      params.value?.clinicalAssessmentGridData;

    this.features = this.clinicalAssessmentGridData?.features;
    this.crossReference = this.clinicalAssessmentGridData?.crossReference;
    this.gridApi = params.api;
  }

  onAddCross({
    opinionUpdates,
  }: {
    opinionOptionKey: string;
    opinionUpdates: CrossReferenceAssessmentUpdates;
  }) {
    if (
      !this.clinicalAssessmentGridData?.permissions
        .CHANGE_CROSS_REFERENCE_OPINION.hasAccess
    ) {
      this.crossReference =
        this.crossReferenceAssessmentsService.getUpdatedLocalClinicalOpinions(
          this.crossReference ?? ({} as CrossReference),
          {
            opinion: { opinion: '', feedbackDescription: '' },
            marketplaceDisputeCount: 0,
            marketplaceApproveCount: 0,
          },
          this.clinicalAssessmentGridData!.user.organization.id,
        );
    } else {
      this.crossReference =
        this.crossReferenceAssessmentsService.getUpdatedLocalClinicalOpinions(
          this.crossReference ?? ({} as CrossReference),
          opinionUpdates,
          this.clinicalAssessmentGridData!.user.organization.id,
        );

      this.crossReference =
        this.crossReferenceAssessmentsService.getUpdatedLocalClinicalNotes(
          this.crossReference ?? ({} as CrossReference),
          opinionUpdates.opinion.feedbackDescription,
          this.clinicalAssessmentGridData!.user.emailAddress,
        );
    }

    if (this.params.addCross) {
      this.params.addCross(this.crossReference!);
    }
  }

  // TODO: Implement injectable service, either through injector token or agInit params.
  onAddNote(noteMessage: string) {
    const id = this.crossReferenceAssessmentsService.getCrossReferenceIdForApi(
      this.crossReference,
      this.clinicalAssessmentGridData,
    );
    this.crossReferenceAssessmentsService
      .addNote(
        id,
        this.clinicalAssessmentGridData!.productId,
        this.crossReference!.toProduct.id,
        noteMessage,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();

    this.crossReference =
      this.crossReferenceAssessmentsService.getUpdatedLocalClinicalNotes(
        this.crossReference ?? ({} as CrossReference),
        noteMessage,
        this.clinicalAssessmentGridData!.user.emailAddress,
      );
  }

  onUpdateOpinion({
    opinionOptionKey,
    opinionUpdates,
  }: {
    opinionOptionKey: string;
    opinionUpdates: CrossReferenceAssessmentUpdates;
    isExplicitSelection: boolean;
  }) {
    const id = this.crossReferenceAssessmentsService.getCrossReferenceIdForApi(
      this.crossReference,
      this.clinicalAssessmentGridData,
    );
    this.crossReferenceAssessmentsService
      .updateOpinion(
        id,
        this.clinicalAssessmentGridData!.productId,
        this.crossReference!.toProduct.id,
        opinionOptionKey,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();

    datadogRum.addAction('Vote on Cross Reference', {
      vote: opinionOptionKey,
      crossReferenceId: id,
    });

    this.updateClinicalAssessmentData(opinionUpdates);
  }

  updateClinicalAssessmentData(
    opinionUpdates: CrossReferenceAssessmentUpdates,
  ) {
    this.crossReference =
      this.crossReferenceAssessmentsService.getUpdatedLocalClinicalOpinions(
        this.crossReference ?? ({} as CrossReference),
        opinionUpdates,
        this.clinicalAssessmentGridData!.user.organization.id,
      );

    this.crossReference =
      this.crossReferenceAssessmentsService.getUpdatedLocalClinicalNotes(
        this.crossReference ?? ({} as CrossReference),
        opinionUpdates.opinion.feedbackDescription,
        this.clinicalAssessmentGridData!.user.emailAddress,
      );

    if (this.params.updateClinicalAssessment) {
      this.params.updateClinicalAssessment(this.crossReference!);
    }
  }

  refresh(): boolean {
    return true;
  }

  ngOnDestroy() {}
}
